import { ChangeDetectionStrategy, Component, Injector } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { BaseApiTemplateService } from 'blg-akaun-ts-lib';
import { PermissionFacade } from '../../../../../../../modules/permission/facades/permission.facade';
import { PermissionSetModel, PermSetLine } from '../../../../../../../models/perm-set.model';
import { PermSetActions } from '../../../../../../../modules/permission/perm-set-controller/actions';
import { PermSetSelectors } from '../../../../../../../modules/permission/perm-set-controller/selectors';
import { PermissionStates } from '../../../../../permission-controller/root';
import { gridOptionsClientSide } from '../../../../../../../listing.utils';
import { ViewColumnComponent } from '../../../../../../../view-column.component';
import { AppConfig } from '../../../../../../../visa';
import { forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { SubSink } from 'subsink2';
import { TranslateService } from "@ngx-translate/core";

interface LocalState {
  deactivateReturn: boolean;
  selectedIndex: number;
  deactivateAdd: boolean;
  deactivateList: boolean;
}

@Component({
  selector: 'app-perm-set-edit-lines',
  templateUrl: './perm-set-edit-lines.component.html',
  styleUrls: ['./perm-set-edit-lines.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ComponentStore]
})

export class PermSetEditLinesComponent extends ViewColumnComponent {

  protected readonly index = 6;
  private localState: LocalState;

  readonly localState$ = this.viewColFacade.selectLocalState(this.index);
  readonly deactivateReturn$ = this.componentStore.select(state => state.deactivateReturn);
  readonly selectedIndex$ = this.componentStore.select(state => (state.selectedIndex));
  readonly deactivateAdd$ = this.componentStore.select(state => state.deactivateAdd);
  readonly deactivateList$ = this.componentStore.select(state => (state.deactivateList));

  // api visa
  apiVisa = AppConfig.apiVisa;

  prevIndex: number;
  private prevLocalState: any;

  // initial ag grid state
  gridApi;
  gridOptions = gridOptionsClientSide;
  columnsDefs;
  rowSelection = 'single';
  rowData$ = [];

  // subSink is to ensure all subscribe calls are unsubscribed to once the component is destroyed
  private subSink = new SubSink;
  searchValue: any;

  selectedPermSet: PermissionSetModel;

  //translation variable
  title: string;
  back_button: string;
  search_text: string;
  code_text: string;
  name_text: string;
  type_text: string;
  default_target_table_text: string;
  default_target_text: string;

  constructor(
    private readonly store: Store<PermissionStates>,
    private viewColFacade: PermissionFacade,
    private componentStore: ComponentStore<LocalState>,
    private injector: Injector
  ) {
    super();
    const translateService = this.injector.get(TranslateService, null);
    if (translateService) {
      translateService.get('labels').subscribe(res => {
        this.title = res.configure_permission_set_lines;
        this.back_button = res.back_button;
        this.search_text = res.search;
        this.code_text = res.code;
        this.name_text = res.name;
        this.type_text = res.type;
        this.default_target_table_text = res.default_target_table;
        this.default_target_text = res.default_target;
      });
    } else {
      // Fallback for applets that don't implement translation
      this.title = 'Configure Permission Set Lines';
      this.back_button = 'Back';
      this.search_text = 'Search';
      this.code_text = 'Code';
      this.name_text = 'Name';
      this.type_text = 'Type';
      this.default_target_table_text = 'Default Target Table';
      this.default_target_text = 'Default Target';
    }
  }

  /**
   * Lifecycle method: Called when component is first initialised.
   */
  ngOnInit(): void {
    // initial configurations for local state
    this.subSink.sink = this.localState$.subscribe( a => {
      this.localState = a;
      this.componentStore.setState(a);
    });
    this.subSink.sink = this.viewColFacade.prevIndex$.subscribe(resolve => this.prevIndex = resolve);
    this.subSink.sink = this.viewColFacade.prevLocalState$().subscribe(resolve => this.prevLocalState = resolve);

    this.columnsDefs = [
      {
        headerName: this.code_text,
        field: 'app_perm_set_line.code',
        cellStyle: {textAlign: 'left'}
      },
      {
        headerName: this.name_text,
        field: 'app_perm_set_line.name',
        cellStyle: {textAlign: 'left'}
      },
      {
        headerName: this.type_text,
        field: 'app_perm_set_line.type',
        cellStyle: {textAlign: 'left'}
      },
      {
        headerName: this.default_target_table_text,
        field: 'app_perm_set_line.target_hdr_table',
        cellStyle: {textAlign: 'left'}
      },
      {
        headerName: this.default_target_text,
        field: 'default_target_hdr_name',
        cellStyle: {textAlign: 'left'},
      }
    ];
  }

  /**
   * This function is called when AG grid is first initialised and initally
   * sets the datasource of the grid.
   *
   * @param params : grid parameters
   */
   onGridReady(params) {
    this.gridApi = params.api;
    this.gridApi.closeToolPanel();

    this.subSink.sink = this.store.select(PermSetSelectors.selectPermSet)
    .subscribe(permSet => {
      this.selectedPermSet = permSet;

      this.rowData$ = [];
      const lines: Observable<PermSetLine>[] = [];

      permSet.permSetLines.forEach(line => {

        let targetViewName = line.default_target_view_name;
        if (targetViewName === "Delivery Branch"){
          targetViewName = "Branch"
        }

        const targetHdrTable = line.app_perm_set_line.target_hdr_table;
        const targetHdrGuid = line.app_perm_set_line.target_hdr_guid;
        if (targetHdrTable && targetHdrGuid) {
          const service: BaseApiTemplateService<any> = this.targetServices.getService(targetViewName);
          const viewField = this.targetServices.getViewField(targetViewName);
          const line$ = service.getByGuid(targetHdrGuid.toString(), this.apiVisa)
          .pipe(
            map( x => {
              line.default_target_hdr_name = x.data[targetHdrTable.toString()][viewField];

              return line;
            })
          )
          lines.push(line$);
        } else if (targetHdrTable && !targetHdrGuid) {
          line.default_target_hdr_name = "ALL";
          lines.push(of(line));
        } else {
          lines.push(of(line));
        }

      });

      // set row data after all line observables complete
      this.subSink.sink = forkJoin(lines)
      .subscribe(x =>
        {
          this.rowData$ = x;
          this.gridApi.setRowData(this.rowData$)
        })
    });
  }

  /**
   * This function is called every time the user clicks on a row in the AG grid.
   *
   * @param entity : the entity on the AG grid that was clicked on
   */
   onRowClicked(entity) {

    // save selected perm set line in store
    this.store.dispatch(PermSetActions.selectPermSetLine({permSetLine: entity}));

    // deactivate add, return and list in this local state
    if (!this.localState.deactivateList) {
      this.viewColFacade.updateInstance(this.index,
        {
          ...this.localState,
          deactivateReturn: true,
          deactivateAdd: true,
          deactivateList: true
        });
      // trigger the edit permission set line view column
      this.viewColFacade.onNext(7);
    }
  }

  /**
   * This functions is called when the user types something in the search bar.
   * Will cause the AG grid to filter its data accordingly.
   *
   */
   onSearch() {
    this.gridApi.setQuickFilter(this.searchValue);
  }

  /**
   * This function is called when the user clears the search bar. Will cause
   * the AG grid to show all the row data accordingly.
   *
   */
   reset() {
    this.searchValue = '';
  }

  /**
   * Return to the previous drawer/view column.
   */
   onReturn() {
    this.viewColFacade.updateInstance(this.prevIndex, {
      ...this.prevLocalState,
      deactivateTemplate: false,
      deactivateReturn: false
    });
    this.viewColFacade.onPrev(this.prevIndex);
  }

  /**
   * Lifecycle method: Called when component is destroyed.
   */
   ngOnDestroy() {
    this.subSink.unsubscribe();
  }

}
